// Voice message functionality types

export interface VoiceRecordingState {
  isRecording: boolean;
  isProcessing: boolean;
  recordingDuration: number;
  audioLevel: number;
  error: string | null;
  hasPermission: boolean;
  isSupported: boolean;
}

export interface AudioTranscriptionRequest {
  audio: Blob;
  language?: 'vi' | 'en';
  maxDuration?: number;
}

export interface AudioTranscriptionResponse {
  success: boolean;
  data?: {
    text: string;
    language?: string;
    confidence?: number;
    duration?: number;
    processing_time_ms: number;
  };
  error?: string;
}

export interface TTSVoice {
  code: string;
  name: string;
  gender: 'male' | 'female';
  language_code: string;
  demo?: string;
  credit_factor?: number;
}

export interface TTSSynthesizePayload {
  text: string;
  voiceCode: string;
  format?: 'mp3' | 'wav' | 'ogg';
  speed?: number;
  sampleRate?: number;
  pitch?: number;
}

export interface VoiceMessageConfig {
  maxRecordingDuration: number; // 60 seconds
  minRecordingDuration?: number; // minimum recording duration
  audioFormat: string; // 'audio/webm;codecs=opus'
  sampleRate: number; // 16000
  channels: number; // 1 (mono)
}

// Component Props Interfaces
export interface VoiceRecorderProps {
  onTranscriptionComplete?: (response: AudioTranscriptionResponse) => void;
  onError?: (error: Error) => void;
  onCancel?: () => void;
  disabled?: boolean;
  config?: Partial<VoiceMessageConfig>;
  className?: string;
  autoStart?: boolean;
}

export interface AudioVisualizerProps {
  audioLevel: number;
  isRecording: boolean;
  duration: number;
  className?: string;
}

export interface VoicePermissionHandlerProps {
  onPermissionGranted: () => void;
  onPermissionDenied: () => void;
  showPermissionDialog: boolean;
}

// Enhanced MessageInput Props
export interface EnhancedMessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxCharacters?: number;
  className?: string;
  enableVoiceInput?: boolean;
  maxRecordingDuration?: number;
  onVoiceTranscription?: (text: string, confidence: number) => void;
  voiceInputConfig?: VoiceMessageConfig;
}

// Voice Recording Hook Return Type
export interface UseVoiceRecordingReturn {
  recordingState: VoiceRecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  transcribeAudio: (audioBlob: Blob) => Promise<AudioTranscriptionResponse>;
  checkMicrophonePermission: () => Promise<boolean>;
  requestMicrophoneAccess: () => Promise<void>;
  isSupported: boolean;
  getRecordingBlob: () => Promise<Blob | null>;
  clearError: () => void;
  resetRecording: () => void;
}

// Audio Processing Types
export interface AudioProcessingOptions {
  sampleRate?: number;
  channels?: number;
  bitRate?: number;
  format?: string;
}

export interface AudioLevelData {
  level: number;
  timestamp: number;
}

// Error Types
export type VoiceErrorType = 
  | 'permission_denied'
  | 'not_supported'
  | 'network_error'
  | 'transcription_failed'
  | 'recording_failed'
  | 'audio_too_long'
  | 'audio_too_short'
  | 'unknown_error';

export interface VoiceError {
  type: VoiceErrorType;
  message: string;
  details?: string;
}

// Default Configuration
export const DEFAULT_VOICE_CONFIG: VoiceMessageConfig = {
  maxRecordingDuration: 60, // 60 seconds
  audioFormat: 'audio/webm;codecs=opus',
  sampleRate: 16000,
  channels: 1
};

// Permission States
export type MicrophonePermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

// Recording States
export type RecordingStatus = 'idle' | 'requesting_permission' | 'recording' | 'processing' | 'completed' | 'error';
